import { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase";
import { getDefaultProfileStatus } from "../utils/profileUtils";

export default function useProfileSync() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);

  const [role, setRole] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [city, setCity] = useState("");
  const [profileLocation, setProfileLocation] = useState({
    state: "",
    county: "",
    city: "",
    town: "",
    zip: "",
    formattedAddress: "",
    placeId: "",
  });
  const [profileStatus, setProfileStatus] = useState("");
  const [statusVisibility, setStatusVisibility] = useState(true);
  const [preferredAreasText, setPreferredAreasText] = useState("");
  const [serviceAreasText, setServiceAreasText] = useState("");
  const [privateAddress, setPrivateAddress] = useState("");
  const [bio, setBio] = useState("");
  const [photoURL, setPhotoURL] = useState("");

  const [verificationStatus, setVerificationStatus] = useState("unverified");
  const [verificationNote, setVerificationNote] = useState("");
  const [verificationDocs, setVerificationDocs] = useState({
    idUrl: "",
    deedUrl: "",
  });

  const [profileHydrated, setProfileHydrated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let profileUnsub = null;

    const reset = () => {
      setProfile(null);
      setRole("");
      setDisplayName("");
      setCity("");
      setProfileLocation({
        state: "",
        county: "",
        city: "",
        town: "",
        zip: "",
        formattedAddress: "",
        placeId: "",
      });
      setProfileStatus("");
      setStatusVisibility(true);
      setPreferredAreasText("");
      setServiceAreasText("");
      setPrivateAddress("");
      setBio("");
      setPhotoURL("");
      setVerificationStatus("unverified");
      setVerificationNote("");
      setVerificationDocs({ idUrl: "", deedUrl: "" });
    };

    const unsub = onAuthStateChanged(auth, (currentUser) => {
      if (process.env.NODE_ENV === "development") {
        console.log("RentDuo auth state changed", {
          signedIn: Boolean(currentUser?.uid),
          uid: currentUser?.uid || "",
          email: currentUser?.email || "",
        });
      }

      if (profileUnsub) profileUnsub();

      setUser(currentUser);
      setProfileHydrated(false);

      if (!currentUser) {
        reset();
        setProfileHydrated(true);
        setLoading(false);
        return;
      }

      const ref = doc(db, "profiles", currentUser.uid);

      profileUnsub = onSnapshot(
        ref,
        (snap) => {
          if (snap.exists()) {
            const data = snap.data() || {};

            const verified =
              Boolean(data.verified) ||
              data.verificationStatus === "verified" ||
              data.verificationStatus === "tenant_verified" ||
              data.verificationStatus === "landlord_verified" ||
              data.verificationStatus === "property_manager_verified";

            const resolvedRole =
              data.verificationStatus === "property_manager_verified"
                ? "property_manager"
                : data.verificationStatus === "landlord_verified"
                ? "landlord"
                : data.role || "";

            setProfile({ ...data, verified });
            setRole(resolvedRole);
            setDisplayName(data.displayName || "");
            setCity(data.city || "");
            setProfileLocation({
              state: data.location?.state || data.state || "",
              county: data.location?.county || "",
              city: data.location?.city || data.city || "",
              town: data.location?.town || "",
              zip: data.location?.zip || "",
              formattedAddress: data.location?.formattedAddress || data.city || "",
              placeId: data.location?.placeId || "",
            });
            setProfileStatus(
              data.profileStatus || (resolvedRole ? getDefaultProfileStatus(resolvedRole) : "")
            );
            setStatusVisibility(data.statusVisibility !== false);
            setPreferredAreasText(data.preferredAreasText || "");
            setServiceAreasText(data.serviceAreasText || "");
            setPrivateAddress(data.privateAddress || "");
            setBio(data.bio || "");
            setPhotoURL(data.photoURL || "");
            setVerificationStatus(
              data.verificationStatus || (verified ? "verified" : "unverified")
            );
            setVerificationNote(data.verificationNote || "");
            setVerificationDocs({
              idUrl: data.verificationDocs?.idUrl || "",
              deedUrl: data.verificationDocs?.deedUrl || "",
            });
          } else {
            reset();
          }

          setProfileHydrated(true);
          setLoading(false);
        },
        (error) => {
          console.error("Profile error:", error);
          reset();
          setProfileHydrated(true);
          setLoading(false);
        }
      );
    });

    return () => {
      if (profileUnsub) profileUnsub();
      unsub();
    };
  }, []);

  return {
    user,
    profile,
    role,
    displayName,
    city,
    profileLocation,
    profileStatus,
    statusVisibility,
    preferredAreasText,
    serviceAreasText,
    privateAddress,
    bio,
    photoURL,
    verificationStatus,
    verificationNote,
    verificationDocs,
    profileHydrated,
    loading,
    setProfile,
    setRole,
    setDisplayName,
    setCity,
    setProfileLocation,
    setProfileStatus,
    setStatusVisibility,
    setPreferredAreasText,
    setServiceAreasText,
    setPrivateAddress,
    setBio,
    setPhotoURL,
    setVerificationStatus,
    setVerificationNote,
    setVerificationDocs,
  };
}
